import { Guid } from "devextreme/common";
import { IUser } from "../../deal/model/types";
import { IBusinessCalendar } from "../../request/model/types";

export interface IGeneratedTasksModel {
  assignedUserName: string;
  approverGroup: string;
  secondApproverGroup: string;
  thirdApproverGroup: string;
  team: string;
  referenceTypeId: string;
  referenceType: string;
  referenceId: string;
  reference: string;
  referenceName: string;
  referenceDate: Date;
  tenor: string;
  frequency?: number;
  numberOfCycles?: number;
  untilDate?: Date;
  dateRollConvention: any;
  location: string;
  tasks: ITaskModel[];
  workflowId: string;
  referenceDateIsFirstDueDate: boolean;
}

export interface ITaskModel {
  source?: string;
  reference?: string;
  taskName?: string;
  templateId?: string;
  taskDescription?: string;
  taskConfigurationId?: string; //
  defaultDueDays?: number;
  defaultAlertDays?: number;
  maxSnoozeDays?: number;
  rescheduleOnExecution?: boolean;
  frequency?: any;
  basedOnIpd?: boolean;
  daysOrBusinessDaysForSla?: any;
  daysOrBusinessDaysForDueDate?: any;
  daysOrBusinessDaysForAlertDate?: any;
  daysOrBusinessDaysForMaxSnooze?: any;
}

export interface IGeneratedTask {
  task: ITaskModel & {
    referenceId: string;
    cycleId: number;
    name: string;
    maxSnoozeDays: number;
    snoozeDate: string;
    dueDate: string;
    alertDate: string;
    rescheduleOnExecution: boolean;
    taskTags: any[];
    assignedUserName: string | null;
    approverGroup: string | null;
    secondApproverGroup: string | null;
    thirdApproverGroup: string | null;
    team: string | null;
    comment: string | null;
    mandateName: string | null;
  };
  taskGroups: ITaskGroup[];
}

export class GeneratedTasksModel implements IGeneratedTasksModel {
  constructor() {
    this.tasks = [];
  }

  assignedUserName: string;
  approverGroup: string;
  secondApproverGroup: string;
  thirdApproverGroup: string;
  team: string;
  referenceTypeId: string;
  referenceType: string;
  referenceId: string;
  reference: string;
  referenceName: string;
  referenceDate: Date;
  tenor: string;
  frequency?: number;
  numberOfCycles?: number;
  untilDate?: Date;
  dateRollConvention: any;
  location: string;
  tasks: any[];
  workflowId: string;
  referenceDateIsFirstDueDate: boolean;
}

export interface IPreProcessedGeneratedTasksModel extends IGeneratedTasksModel {
  locationArray: Array<string>;
  taskTemplateId: string;
  useTemplate: boolean;
}

export class PreProcessedGeneratedTasksModel extends GeneratedTasksModel implements IPreProcessedGeneratedTasksModel {
  locationArray: Array<string>;
  taskTemplateId: string;
  useTemplate: boolean;
}

export interface ITaskResponse {
  unreadCommentCount?: number;
  unreadUrlCount?: number;
  parentTaskId: string;
  parentUrls: string[];
  id: string;
  parentId: string;
  hasParent: boolean;
  subTasks: ITaskResponse[];
  batchId: string;
  status: string;
  sequenceNumber: number;
  taskId: string;
  dueDate: Date;
  alertDate?: Date;
  dateCompleted?: Date;
  referenceType: string;
  referenceName: string;
  mandate: string;
  // Guid? TaskConfigurationId
  name: string;
  description: string;
  taskSLADays: number;
  taskSLAHours?: number;
  taskSLAMinutes?: number;
  approverName: string;
  rejectorName: string;
  completerName: string;
  comment: string;
  commentCount: number;
  riskAllocation: string;
  taskTags: ITaskTag[];
  calendars: IBusinessCalendar[];
  taskType: string;
  taskData: string;
  prettifiedTaskData: string;
  prettifiedTaskDataArray: Array<any>;
  director: string;
  secondDirector: string;
  assignedUserName: string;
  assignedUserId: string;
  team: string;
  taskGroups: ITaskGroup[];
  isOverDue: boolean;
  createdOn: Date;
  createdBy: string;
  isActive: boolean;
  action: string;
  urls: string[];
  redoTaskId: string;
  isTeamBound: boolean;
  tooltip: string;
}

// In your types file or at the top of the component
interface ITaskResponseExtended extends ITaskResponse {
  amount?: number | string | null;
  // Add other extracted fields as needed
}

export class TaskResponse implements ITaskResponse {
  parentTaskId: string;
  parentUrls: string[];
  id: string;
  parentId: string;
  hasParent: boolean;
  subTasks: ITaskResponse[];
  batchId: string;
  status: string;
  sequenceNumber: number;
  taskId: string;
  dueDate: Date;
  alertDate?: Date;
  dateCompleted?: Date;
  referenceType: string;
  referenceName: string;
  mandate: string;
  // Guid? TaskConfigurationId
  name: string;
  description: string;
  taskSLADays: number;
  taskSLAHours?: number;
   taskSLAMinutes?: number;
  approverName: string;
  rejectorName: string;
  completerName: string;
  comment: string;
  commentCount: number;
  riskAllocation: string;
  taskTags: ITaskTag[];
  // int? SameDayCutOffHours
  // int? SameDayCutOffMinutes
  calendars: IBusinessCalendar[];
  taskType: string;
  taskData: string;
  prettifiedTaskData: string;
  prettifiedTaskDataArray: Array<any>;
  // DateTime? RequestReceivedDateTime
  director: string;
  secondDirector: string;
  assignedUserName: string;
  team: string;
  assignedUserId: string;
  taskGroups: ITaskGroup[];
  isOverDue: boolean;
  createdOn: Date;
  createdBy: string;
  isActive: boolean;
  action: string;
  urls: string[];
  isClosedStatus: boolean;
  redoTaskId: string;
  isTeamBound: boolean;
  tooltip: string;
}

export interface ITaskRequest extends ITaskResponse {
  id: string;
  assignedUserId: string;
  taskConfigurationId: string;
  taskGenerationMetadataId: string;
  approverId: string;
  rejectorId: string;
  completerId: string;
  templateId: string;
  linkedTemplateName: string;
  locations: string;
  clientName: string;
  useTaskOffsetsForDueDate: boolean;
  isUrgent: boolean;
  requestJsonData: string;
  serviceRequestType: string;
  files: FormData;
}

export class TaskRequest extends TaskResponse implements ITaskRequest {
  taskConfigurationId: string;
  taskGenerationMetadataId: string;
  approverId: string;
  rejectorId: string;
  completerId: string;
  templateId: string;
  linkedTemplateName: string;
  locations: string;
  clientName: string;
  useTaskOffsetsForDueDate: boolean;
  isUrgent: boolean;
  requestJsonData: string;
  serviceRequestType: string;
  files: FormData;
}

export interface ITaskComment {
  id: string;
  taskId: string;
  commentId: string;
  headline: string;
  comment: string;
  commenterId: string;
  commenterName: string;
  createdBy: string;
  createdOn: Date;
}

export class TaskComment implements ITaskComment {
  id: string;
  taskId: string;
  commentId: string;
  headline: string;
  comment: string;
  commenterId: string;
  commenterName: string;
  createdBy: string;
  createdOn: Date;
}

export interface ITaskGroup {
  GroupType: string;
  GroupName: string;
  ApproverLevel: number;
}




export interface ITaskTag {
  key: string;
  value: string;
}
export class TaskTag implements ITaskTag
{
  key: string;
  value: string;

}



export interface ITasksSummary{
  statusName: string;
  statusCount: number;
}

export interface ITasksNavigation {
  startDate: Date;
  endDate: Date;
  taskStatus: string;
  name: string;
  taskType: string;
  myTasks: boolean;
}

export class TasksNavigation implements ITasksNavigation {
  startDate: Date;
  endDate: Date;
  taskStatus: string;
  name: string;
  taskType: string;
  myTasks: boolean;
}

export interface ITaskCommentPost {
  taskId: string;
  headline: string;
  comment: string;
  commenterId?: string | number;
  logEntryTypeId: string;
  actionDate: Date;
  mentions: Array<string>;
}

export class TaskCommentPost implements ITaskCommentPost {
  taskId: string;
  headline: string;
  comment: string;
  commenterId?: string | number;
  logEntryTypeId: string;
  actionDate: Date;
  mentions: Array<string> = new Array<string>();
}

export interface IValidationResult {
  isValid: boolean;
  field: string;
  value: string;
}

export interface IGroupItem {
  column: string;
  summaryType: string;
}

export interface ITransactionImportLog {
  fileName: string;
  message: string;
  transactionDate: string;
  transaction: any;
  exception: any;
  updatedTimestamp: string;
}

export interface IGridColumn {
  dataType: string;
  field?: string;
  title: string;
  textFormat?: string | Function;
  dataFormat?: object;
  precision?: number;
  valueCalculator?: any;
  columnType?: string;
  lookupConfig?: {
    dataSource: any;
    valueExpression?: any;
    displayExpression?: any;
  };
  setCellValue?: Function;
  allowEditing?: boolean;
  hasCustomFilter?: boolean;
  filterDataSource?: any;
  showEditorAlways?: boolean;
  allowSearch?: boolean;
  allowFiltering?: boolean;
  allowSorting?: boolean; // Added missing allowSorting property
  allowGrouping?: boolean;
  icons?: string[];
  visible?: boolean;
  cellTemplate?: string;
  calculateFilterExpression?: Function;
  width?: number;
  fixed?: boolean;
  fixedPosition?: 'left' | 'right';
}

export interface IGridSummary {
  name: string;
  calculateFunction?: Function;
  totalItems: ISummaryTotalItem[] | null;
  groupItems: IGroupItem[] | null;
}

export interface ISummaryTotalItem {
  type: string;
  name: string;
  summaryType: string;
  valueFormat?: string | null;
  displayFormat?: string | null;
  customizeText?: Function | null;
  column: string;
}

export interface IKeyValuePair {
  key: any;
  value: any;
}

export interface ITab {
  id: number;
  text: string;
  icon: string;
  content: any;
}

export interface ICellInfo {
  cellElement?: any;
  rowIndex?: number;
  columnIndex?: number;
}

export interface ISelectedRange {
  startRowIndex?: number;
  endRowIndex?: number;
  startColumnIndex?: number;
  endColumnIndex?: number;
}

export interface ITaskVoucherData {
  requestType: string;
  feesPaidToMS: boolean;
  bank: string;
  debitBank: string;
  voucherAmount: number;
  loanNumber: string;
  billableToClient: boolean;
  fedReferenceNumbers: string;
  treasuryProvideFedRefNumbers: boolean;
  wireInformationForFedRefNumbers?: string;
  voucherDueDate: Date;
  director: string;
  secondDirector: string;
  paymentMethod: string;
  isUrgent: boolean;
  currency: string;
}

export class TaskVoucherData implements ITaskVoucherData {
  requestType: string;
  feesPaidToMS: boolean = false;
  bank: string;
  debitBank: string;
  voucherAmount: number;
  loanNumber: string;
  billableToClient: boolean = false;
  fedReferenceNumbers: string = '';
  treasuryProvideFedRefNumbers: boolean = false;
  wireInformationForFedRefNumbers: string = '';
  voucherDueDate: Date;
  director: string;
  secondDirector: string;
  paymentMethod: string;
  isUrgent: boolean = false;
  currency: string = 'USD';
}

export interface IPaymentMethod {
  text: string;
  method: string;
  key: number;
}

export class PaymentMethod implements IPaymentMethod {
  text: string;
  method: string;
  key: number;
}

export interface ITaskPipelineData {
  dealName: string;
  borrowerName: string;
  client: string;
  requestType: string;
  closingDate: Date;
  amAssignee: string;
  amAssigneeId?: string;
  lsaAssignment?: string;
  loanNumbers?: string;
  loanNumbersData?: any[];
}

export class TaskPipelineData implements ITaskPipelineData {
  dealName: string;
  borrowerName: string;
  client: string;
  requestType: string;
  closingDate: Date;
  amAssignee: string;
  amAssigneeId?: string;
  lsaAssignment?: string;
  loanNumbers?: string;
  loanNumbersData?: any[] = [];
}


export interface ITaskBorrowerDrawData {
  requestType: string;
  draw: number;
  loanNumber: string;
  dateFunded: Date;
  description: string;
  invoiceBorrowerOrClient: boolean;
  responsiblePerson: string;
  responsibleReviewer: string;
  drawFee: number;
  drawAmount: number;
}

export class TaskBorrowerDrawData implements ITaskBorrowerDrawData {
  requestType: string;
  draw: number;
  loanNumber: string;
  dateFunded: Date;
  description: string;
  invoiceBorrowerOrClient: boolean;
  responsiblePerson: string;
  responsibleReviewer: string;
  drawFee: number;
  drawAmount: number;
}

export interface IPayoffPayDownRequestData {
  loanNumbers: Array<{key: string, loanNumber: string}>;
  requestType: string;
  comments: string;
  dateNeeded: Date;
  expectedPayoffDate: Date;
  trigger: string;
  isRushJob: boolean;
  isRushFee: boolean;
  transactionType: string;
  nextTaxDueDate?: Date | null;
  reservesOnLoan: boolean;
}

export class PayoffPayDownRequestData implements IPayoffPayDownRequestData {
  loanNumbers: Array<{key: string, loanNumber: string}> = [];
  requestType: string;
  comments: string;
  dateNeeded: Date;
  expectedPayoffDate: Date;
  trigger: string;
  isRushJob: boolean = false;
  isRushFee: boolean = false;
  transactionType: string;
  reservesOnLoan: boolean = false;
}

export interface IServicingRequestData {
  loanNumber: string;
  requestType: string;
  dateReceived: Date;
  description: string;
  urgent: boolean;
}

export class ServicingRequestData implements IServicingRequestData {
  loanNumber: string;
  requestType: string;
  dateReceived: Date;
  description: string;
  urgent: boolean = false;
}

export interface IServiceRequest {
  mandate: string;
  referenceName: string;
  referenceType: string;
  location: string;
  serviceRequestType: string;
  requestJsonData: string;
  isUrgent: boolean;
  description: string;
  dueDate: Date;
}

export class ServiceRequest implements IServiceRequest {
  mandate: string;
  referenceName: string;
  referenceType: string;
  location: string;
  serviceRequestType: string;
  requestJsonData: string;
  isUrgent: boolean = false;
  description: string;
  dueDate: Date;
}

export interface ITaskModificationData {
  loanNumbers: string[] | { key?: string; loanNumber: string }[];
  requestType: string;
  notesAffected: number;
  investor: string;
  documentsReceivedDate: Date;
  modificationDate: Date;
  newInvestor?: boolean;
  isUrgent?: boolean;
  dealName?: string;
}

export class TaskModificationData implements ITaskModificationData {
  loanNumbers: string[] | { key?: string; loanNumber: string }[] = [];
  requestType: string;
  notesAffected: number;
  investor: string;
  documentsReceivedDate: Date;
  modificationDate: Date;
  newInvestor: boolean = false;
  isUrgent: boolean = false;
  dealName?: string;
}

export interface IRequestDataBase {
  requestType: string;
  loanNumber: string;
  description: string;
  amount: number;
  currency: any;
}

export class RequestDataBase implements IRequestDataBase {
  requestType: string;
  loanNumber: string;
  description: string;
  amount: number;
  currency: any;
}

export interface ITreasuryRequestData extends IRequestDataBase {
  dateNeeded: Date;
}

export class TreasuryRequestData implements ITreasuryRequestData, IRequestDataBase {
  requestType: string;
  loanNumber: string;
  dateNeeded: Date;
  description: string;
  amount: number;
  currency: any;
}

export interface IDefaultNotificationData {
  requestType: string;
  loanNumber: string;
  EndOfTheDate: Date;
  suspendBilling: boolean;
  description: string;
  selectedLoanBorrowers?: Array<{ loanNumber: string; borrower: string; dealNumber: string }>;
  invoice: boolean;
}

export class DefaultNotificationData implements IDefaultNotificationData {
  requestType: string;
  loanNumber: string;
  EndOfTheDate: Date;
  suspendBilling: boolean = false;
  description: string;
  selectedLoanBorrowers?: Array<{ loanNumber: string; borrower: string; dealNumber: string }> = [];
  invoice: boolean = false;
}

export interface IVendorRequestData {
  requestType: string;
  loanNumber: string;
  dateNeeded: Date;
  payeeName: string;
  disbursementType: string;
  isRushJob: boolean;
}

export class VendorRequestData implements IVendorRequestData {
  requestType: string;
  loanNumber: string;
  dateNeeded: Date;
  payeeName: string;
  disbursementType: string;
  isRushJob: boolean = false;
}

export interface IButtonConfig {
  id: string;
  name?: string;
  icon: string;
  text: string;
  cssClass?: string;
  disabled: boolean;
  hint?: string;
  action?: (e: any) => void;
}

export interface IDealData {
  name: string;
  dealNumber: string;
  loanNumbers: string[];
  isActive: boolean;
}

export interface IUnreadCounts{
  taskId: Guid;
  unreadComments: number;
  unreadUrls: number;
  totalUnread: number;
}

export interface IBusinessDayQueryModel{
  date: string;
  tenor: string;
  location: string;
  dateRollConvention: string;
}

export interface IBusinessDayResultsModel{
  result: IBusinessDay;
  warinings: string[];
}

export interface IBusinessDay {
  dateUnadjusted: Date;
  dateAdjusted: Date;
  dateCalculated: Date;
}

export interface ILoanBorrower {
  loanNumber: string;
  dealNumber: string;
  borrower: string;
  status: string;
}
